// connectionWarning - a stale connection is a warning, never a failure.
//
// A provider credential that has expired is routine maintenance. Showing
// it as a red error, with the vendor's raw diagnostic text, does three
// things wrong: it makes a working product look broken, it discards data
// that is still true, and it puts an app registration id on screen in
// front of whoever is watching.
//
// This maps a raw provider error to a short sentence a customer can act
// on. The raw text is never returned - it belongs in the sync state
// record, where an administrator can read it and a demo audience cannot.

/* Matched on the provider's own error code. Anything unrecognised gets
   the generic sentence rather than being passed through, because "we did
   not recognise this" is not a reason to print a vendor stack trace. */
const PATTERNS = [
  { re: /AADSTS7000215|AADSTS7000222|AADSTS700016|invalid_client/i,
    text: 'This connection needs reauthorising. Sign in again under Connections to refresh it.' },
  { re: /AADSTS50076|AADSTS50079|interaction_required/i,
    text: 'This connection needs an administrator to complete sign-in again.' },
  { re: /AADSTS65001|consent_required|insufficient.privileges|Authorization_RequestDenied/i,
    text: 'This connection is missing a permission it needs. Check the consent banner under Connections.' },
  { re: /credential expired|token expired|expired/i,
    text: 'The stored credential for this connection has expired.' },
  { re: /throttl|429|TooManyRequests/i,
    text: 'The provider is rate limiting requests. This usually clears on the next scheduled run.' },
  { re: /timeout|ETIMEDOUT|ECONNRESET|network/i,
    text: 'The provider could not be reached on the last attempt.' },
];

/* Returns a short sentence, or null when there is nothing to warn about.
   NEVER returns the raw input. */
export function connectionWarning(raw) {
  if (!raw) return null;
  const s = String(raw);
  for (const p of PATTERNS) if (p.re.test(s)) return p.text;
  return 'This connection could not be reached on the last attempt.';
}

/* "Showing data as of 1 September" - so a reader knows the figures are
   real and simply not current, rather than wondering what they are. */
export function asOfLabel(when) {
  if (!when) return null;
  const d = new Date(when);
  if (isNaN(d.getTime())) return null;
  return 'Showing data as of ' + d.toLocaleDateString(undefined,
    { day: 'numeric', month: 'long', year: 'numeric' });
}
